import { GIButton, Modal } from 'app-components';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { ModalContextType } from '@contexts/modalContext';
import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';

import { fetchEnrollmentAndEnrolleeDetails } from '@actions/memberPortal/enrollmentsActionCreators';
import { TEXTAREA_MAX_LENGTH, TEXTAREA_MIN_LENGTH } from '@constants/enrollments/enrollments.constants';
import { ConditionalRendering } from '../../utils';
import { validateTextArea } from '../common/enrollmentUtils';
import { IEnrollment, IEnrollmentReinstateModal } from '../common/types';
import { MODAL_KEYS } from './modalKeys';
import {
    StyledCharacterCounter,
    StyledFooterContainer,
    StyledModalContent,
    StyledTextAreaContainer,
} from './ModalStyles';
import { ModalTransactionError, ModalTransactionSuccess } from './ModalSuccessError';

interface IModalProps extends ModalContextType {
    enrollment: IEnrollment;
    reinstateenrollment: IEnrollmentReinstateModal;
}

interface ICommentState {
    overrideCommentText: string;
}

interface ISend834State {
    send_In_834: boolean;
}

const ReinstateEnrollmentModal = ({
    closeModal,
    enrollment,
    reinstateenrollment: {
        handleSubmit,
        content: { title, body },
        successMessage,
        errorMessage,
        clearState,
    },
}: IModalProps) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string }>();
    const { isSuccess, isError, data } = useSelector((state: any) => state?.memberEnrollment?.enrollmentRequest);

    const [value, setValue] = useState<ICommentState>({ overrideCommentText: '' });
    const [checked, setChecked] = useState<ISend834State>({ send_In_834: true });
    const maxChars = useMemo(() => TEXTAREA_MAX_LENGTH - value.overrideCommentText.length, [value.overrideCommentText]);

    const handleClose = e => {
        if (isSuccess) {
            const formData = new FormData();
            formData.append('encHouseholdId', id);
            formData.append('csrftoken', window.csrf_token);
            dispatch(fetchEnrollmentAndEnrolleeDetails(formData));
        }
        closeModal(MODAL_KEYS.REINSTATE_ENROLLMENT);
        clearState();
        e.preventDefault();
    };

    const handleContinue = () => {
        handleSubmit({
            enrollmentId: enrollment.encEnrollmentId,
            overrideCommentText: value.overrideCommentText,
            caseNumber: enrollment.ssapCaseNumber,
            planType: enrollment.planType,
            houseHoldId: id,
            send_In_834: checked.send_In_834,
        });
    };

    const onFieldChange = inputValue => {
        const value = validateTextArea(inputValue);
        setValue({ overrideCommentText: value });
    };

    return (
        <Modal id={MODAL_KEYS.REINSTATE_ENROLLMENT} data-testid="reinstate-enrollment-modal">
            <Modal.Header
                id="reinstateEnrollmentModalTitle"
                titleId={title}
                onHide={handleClose}
                data-testid="modal-header"
            />
            <ConditionalRendering isTrue={!isSuccess}>
                <Modal.Body data-testid="modal-body">
                    <>
                        <StyledModalContent data-testid="modal-content">{body}</StyledModalContent>
                        <Form data-testid="ReinstateEnrollmentModalform">
                            <StyledTextAreaContainer data-testid="textarea-container">
                                <UniversalInput
                                    label={{
                                        text: intl.formatMessage({ id: 'member.enrollment.modal.textarea.label' }),
                                        labelClasses: ['spl__width--01'],
                                    }}
                                    inputData={{
                                        isInputRequired: true,
                                        type: INPUT_TYPES.TEXTAREA,
                                        name: 'overrideCommentText',
                                        value: value.overrideCommentText,
                                        liClass: ['text-left'],
                                        currentData: value,
                                        placeholder: intl.formatMessage({
                                            id: 'member.enrollment.modal.textarea.placeholder',
                                        }),
                                        rows: 8,
                                        validationType: VALIDATION_TYPES.LENGTH,
                                        valuesToMatch: [TEXTAREA_MIN_LENGTH, TEXTAREA_MAX_LENGTH],
                                    }}
                                    errorClasses={['margin-l-0']}
                                    inputActions={{
                                        onFieldChange: ({ overrideCommentText }) => {
                                            onFieldChange(overrideCommentText);
                                        },
                                    }}
                                    data-testid="textarea-input"
                                />
                            </StyledTextAreaContainer>
                            <StyledCharacterCounter data-testid="character-counter">
                                {maxChars} {intl.formatMessage({ id: 'member.enrollment.modal.textLeft' })}
                            </StyledCharacterCounter>
                        </Form>
                    </>
                </Modal.Body>
                <Modal.Controls data-testid="modal-controls">
                    <StyledFooterContainer data-testid="footer-container">
                        <Form formName="send_In_834_form" data-testid="send-in-834-form">
                            <UniversalInput
                                label={{
                                    text: intl.formatMessage({ id: 'member.enrollment.modal.checkbox.send_In_834' }),
                                }}
                                inputData={{
                                    isInputRequired: false,
                                    type: INPUT_TYPES.CHECKBOX,
                                    name: 'send_In_834',
                                    classes: ['spl__width--01'],
                                    checked: checked.send_In_834,
                                    value: checked.send_In_834,
                                    currentData: checked,
                                    fields: [],
                                }}
                                inputActions={{
                                    onFieldChange: ({ send_In_834 }) => {
                                        setChecked({ send_In_834 });
                                    },
                                }}
                                data-testid="send-in-834-checkbox"
                            />
                        </Form>
                        <GIButton
                            isDisabled={!value.overrideCommentText.length || isSuccess || isError}
                            id="button"
                            onClickHandler={handleContinue}
                            variant="primary"
                            labelId="button.continue"
                            data-testid="continue-button"
                        />
                        <GIButton
                            id="button"
                            onClickHandler={handleClose}
                            variant="secondary"
                            labelId="button.cancel"
                            data-testid="cancel-button"
                        />
                    </StyledFooterContainer>
                </Modal.Controls>
            </ConditionalRendering>

            <ConditionalRendering isTrue={isSuccess || isError}>
                <>
                    <Modal.Body data-testid="modal-body">
                        <ModalTransactionSuccess message={successMessage} isTrue={isSuccess} />
                        <ModalTransactionError
                            data={data}
                            isTrue={isError}
                            heading="cap.applications.modal.csrsubmissionfailtitle"
                        />
                    </Modal.Body>
                    <Modal.Controls data-testid="modal-controls">
                        <GIButton
                            id="button"
                            onClickHandler={handleClose}
                            variant="secondary"
                            labelId="button.ok"
                            data-testid="ok-button"
                        />
                    </Modal.Controls>
                </>
            </ConditionalRendering>
        </Modal>
    );
};

export default ReinstateEnrollmentModal;
